/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService, ServiceContext } from 'src/core/utils/createService';
import { entityRoleSwaggerToEntityRoleStore } from 'src/luis/core/services/entities/mappers';
import { createEntityRoleStore } from 'src/luis/core/stores/entityRoleStore';
import { EntityRoleSwagger } from 'src/luis/projects/entities/types/swagger/EntityRoleSwagger';
import { getEntityUrlName } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';

/**
 * Represents a service to perform CRUD operations for entity roles.
 *
 * @param entityType The type of the entity.
 */
export const getEntityRoleService = createService(
    ({ context, getEnvironment }: ServiceContext & { context: { entityType: ModelType } }) => {
        const { apiUrl, isProduction } = getEnvironment();
        const apiOptions = luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, isProduction)
            .build();

        /**
         * Creates a common url scheme for this service.
         *
         * @param entityId The entity id.
         * @param roleId An optional entity role id.
         * @returns A url string to call.
         */
        const getUrl = (entityId: LuisId, roleId: LuisId = ''): string =>
            urlBuilder()
                .baseUrl(apiUrl)
                .app(context.appId)
                .version(context.versionId)
                .resource(getEntityUrlName(context.entityType), `${entityId}`)
                .resource('roles', `${roleId || ''}`)
                .build();

        /**
         * Fetches all the roles that belong to an entity.
         *
         * @param entityId The id of the entity holding the roles.
         *
         * @returns The list of entity roles.
         */
        const getEntityRoles = async ({ entityId }: { entityId: LuisId }): Promise<readonly EntityRoleStore[]> => {
            const { data: entityRoles } = await httpService.get<readonly EntityRoleSwagger[]>(getUrl(entityId), apiOptions);

            return entityRoles.map(entityRoleSwaggerToEntityRoleStore);
        };

        /**
         * Fetches an entity role given the id.
         *
         * @param entityId The id of the entity holding the role.
         * @param roleId The name of the role to be added to the entity.
         *
         * @returns The entity role with the given id.
         */
        const getEntityRole = async ({ entityId, roleId }: { entityId: LuisId; roleId: LuisId }): Promise<EntityRoleStore> => {
            const { data: entityRole } = await httpService.get<EntityRoleStore>(getUrl(entityId, roleId), apiOptions);

            return entityRole;
        };

        /**
         * Adds a new role to the given entity.
         *
         * @param entityId The id of the entity to add the new role to.
         * @param roleName The name of the role to be added to the entity.
         *
         * @returns The newly created entity role.
         */
        const addEntityRole = async ({ entityId, roleName }: { entityId: LuisId; roleName: string }): Promise<EntityRoleStore> => {
            const { data: roleId } = await httpService.post<string>(getUrl(entityId), { name: roleName }, apiOptions);

            return createEntityRoleStore(roleId, { name: roleName });
        };

        /**
         * Deletes the role from the given entity.
         *
         * @param id The id of the intent to delete.
         */
        const deleteEntityRole = async ({ entityId, roleId }: { entityId: LuisId; roleId: LuisId }): Promise<void> => {
            await httpService.delete(getUrl(entityId, roleId), apiOptions);
        };

        /**
         * Renames a give entity role.
         *
         * @param entityId The id of the entity to add the new role to.
         * @param roleName The name of the role to be added to the entity.
         * @param newName The new name for the entity role.
         */
        const renameEntityRole = async ({
            entityId,
            roleId,
            newName
        }: {
            entityId: LuisId;
            roleId: LuisId;
            newName: string;
        }): Promise<void> => {
            await httpService.put(getUrl(entityId, roleId), { name: newName }, apiOptions);
        };

        return { getEntityRoles, getEntityRole, addEntityRole, deleteEntityRole, renameEntityRole };
    }
);
